import { useState } from "react";
import styled from "styled-components";
import { MARKETING } from "../theme/ThemeContext";
import { useAuth } from "../context/AuthContext";
import {
  MarketingGlobalStyle,
  MarketingPage,
  MarketingFooter,
  Container,
  Nav,
  NavInner,
  NavLinks,
  NavLink,
  PrimaryButton,
  OutlineDarkLink,
  Section,
  Serif,
  H1,
  H2,
  Eyebrow,
  SectionHead,
  CTAPanel,
  CTAButtons,
  FaqItem,
  Reveal,
  DemoModal,
  LoginModal,
  SignupModal,
  MarketingNavActions,
  NavLogo,
  IconCheck,
  LOGO,
  SANS,
  SERIF,
} from "./LandingPage";

/* Public pricing page. Same chrome and palette as the landing page; the plan
   cards and the credits band are the only pieces defined here. */

const C = MARKETING;

export const ANNUAL_DISCOUNT = 0.1;
export const TRIAL_DAYS = 7;
const TRIAL_CREDITS = 1000;

/* Target list add-on: one-off setup per company, then a monthly amount added to the subscription. */
export const ADDON_SETUP_USD = 5;
export const ADDON_MONTHLY_USD = 2;
/* The same thing paid in credits, for the side-by-side. */
const ADDON_SETUP_CREDITS = 1000;
const ADDON_MONTHLY_CREDITS = 200;
const CREDIT_USD = 0.01;
/* Credit top-up add-on: USD per block of credits. */
export const TOPUP_BLOCK_CREDITS = 1000;
export const TOPUP_BLOCK_USD = 10;

/* The $0 one-off trial and the four plans. Prices in USD per month; credits top up monthly on either cycle. */
export const PLANS = [
  {
    id: "trial",
    name: "Trial",
    tagline: "Try candidate search on a real role.",
    monthly: 0,
    trial: true,
    credits: TRIAL_CREDITS,
    cta: "Start free trial",
    intro: null,
    features: [
      "Find candidates from a job description",
      "Or describe the person you need",
      "Up to 20 matching people per search",
      `${TRIAL_DAYS} days, no card needed`,
    ],
  },
  {
    id: "starter",
    name: "Starter",
    tagline: "For one recruiter running their own desk.",
    monthly: 149,
    credits: 15000,
    cta: "Choose Starter",
    intro: null,
    features: [
      "Track jobs from any company website",
      "AI candidate search",
      "Email lookup",
      "AI outreach drafts",
      "Email support",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    tagline: "For a recruiter working more than one market.",
    monthly: 249,
    credits: 30000,
    featured: true,
    cta: "Choose Growth",
    intro: "Everything in Starter, plus",
    features: [
      "Track jobs from third-party job boards",
      "Run 2 candidate searches at once",
      "Similar-candidate search",
      "Email alerts",
    ],
  },
  {
    id: "scale",
    name: "Scale",
    tagline: "For a heavy desk that never slows down.",
    monthly: 399,
    credits: 50000,
    cta: "Choose Scale",
    intro: "Everything in Growth, plus",
    features: [
      "Run up to 5 candidate searches at once",
      "Credits roll over (3 months)",
      "Priority support",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "For firms with their own security and procurement needs.",
    monthly: null,
    credits: null,
    cta: "Talk to us",
    intro: "Everything in Scale, plus",
    features: [
      "Custom credit volume",
      "Custom company job coverage",
      "ATS or CRM integration",
      "Multiple seats",
      "Dedicated onboarding",
    ],
  },
];

const FAQS = [
  {
    question: `How does the ${TRIAL_DAYS}-day free trial work?`,
    answer: `Every new account gets ${TRIAL_DAYS} days and ${TRIAL_CREDITS.toLocaleString("en-US")} credits in its own workspace, with no card needed. Pick a plan whenever you're ready; until then the trial credits are yours to spend.`,
  },
  {
    question: "What is a credit?",
    answer:
      "One balance that every tool draws from: candidate searches, email lookups, CV matching, contact research, and outreach drafts. Each action shows what it will cost before you run it, and your balance is always visible in the sidebar.",
  },
  {
    question: "What happens when I run out?",
    answer:
      "New runs pause until your next top-up, so you are never surprised by a bill. Upgrade at any time and the larger allowance applies straight away, with the change prorated to the day.",
  },
  {
    question: "Do unused credits carry over?",
    answer:
      "On Starter and Growth the allowance resets each month. Scale carries unused credits for three months, so a quiet month is not a wasted one.",
  },
  {
    question: "How does yearly billing work?",
    answer: `You pay for twelve months up front at ${Math.round(ANNUAL_DISCOUNT * 100)}% less than the monthly price. Credits still top up every month.`,
  },
  {
    question: "Can I cancel?",
    answer:
      "Yes, from your account settings at any time. Your plan stays active until the end of the period you have paid for, and your data stays yours.",
  },
  {
    question: "Which payment methods do you take?",
    answer:
      "Visa, Mastercard, American Express, Apple Pay, and Google Pay, billed in US dollars. Enterprise plans can be invoiced and paid by bank transfer.",
  },
];

export const usd = (n) => "$" + Number(n).toLocaleString("en-US");
const credits = (n) => Number(n).toLocaleString("en-US");
export const annualTotal = (monthly) => Math.round(monthly * 12 * (1 - ANNUAL_DISCOUNT));

/* --- styles ---------------------------------------------------------------- */

const Hero = styled.header`
  position: relative;
  text-align: center;
  padding: 96px 0 56px;
  overflow: hidden;

  @media (max-width: 700px) {
    padding: 64px 0 40px;
  }
`;

const Toggle = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 34px;
  padding: 4px;
  border-radius: 999px;
  background: ${C.wash};
  border: 1px solid ${C.line};
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: ${({ $on }) => ($on ? C.deep : C.body)};
  background: ${({ $on }) => ($on ? C.onDark : "transparent")};
  box-shadow: ${({ $on }) => ($on ? `0 1px 2px color-mix(in srgb, ${C.deep} 10%, transparent)` : "none")};
  transition:
    background 0.18s ease,
    color 0.18s ease;

  &:focus-visible {
    outline: 2px solid ${C.blue};
    outline-offset: 2px;
  }
`;

const SavePill = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${C.orangeWash};
  color: ${C.orangeInk};
`;

const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: 1320px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

/* Subgrid rows line prices, credits and buttons up across cards: keep the 8 in-flow children in this order. */
const Card = styled.article`
  position: relative;
  display: grid;
  grid-template-rows: subgrid;
  grid-row: span 8;
  row-gap: 0;
  align-content: start;
  padding: 30px 26px 28px;
  border-radius: 22px;
  background: ${({ $featured }) => ($featured ? `color-mix(in srgb, ${C.orangeWash} 60%, ${C.onDark})` : C.onDark)};
  color: ${C.ink};
  border: 1px solid ${({ $featured }) => ($featured ? C.orange : C.line)};
  box-shadow: ${({ $featured }) => ($featured ? `0 0 0 1px ${C.orange}` : "none")};
`;

const Tag = styled.span`
  position: absolute;
  top: -13px;
  left: 50%;
  transform: translateX(-50%);
  height: 26px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: ${C.orange};
  color: ${C.onDark};
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
`;

const PlanName = styled.h3`
  font-family: ${SANS};
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.015em;
  margin: 0;
`;

const Tagline = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.5;
  min-height: 42px;
  color: ${C.body};
`;

const Price = styled.div`
  margin-top: 26px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-family: ${SANS};
  font-variant-numeric: tabular-nums;

  .amount {
    font-size: 44px;
    font-weight: 600;
    letter-spacing: -0.035em;
    line-height: 1;
  }
  .per {
    font-size: 14px;
    color: ${C.body};
  }
`;

const BilledNote = styled.div`
  margin-top: 8px;
  min-height: 18px;
  font-size: 13px;
  color: ${C.muted};
`;

const Allowance = styled.div`
  margin-top: 18px;
  font-family: ${SERIF};
  font-style: italic;
  font-size: 23px;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: ${C.orange};
  font-variant-numeric: tabular-nums;
`;

const Cta = styled.button`
  margin-top: 22px;
  height: 46px;
  width: 100%;
  border-radius: 999px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.18s ease,
    color 0.18s ease;

  ${({ $tone }) =>
    $tone === "orange"
      ? `
    border: 0;
    background: ${C.orange};
    color: ${C.onDark};
    &:hover { background: ${C.orangeBright}; }
  `
      : `
    background: ${C.onDark};
    color: ${C.ink};
    border: 1px solid ${C.border};
    &:hover { background: ${C.wash}; color: ${C.blue}; }
  `}

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${C.blue};
    outline-offset: 2px;
  }
`;

const Intro = styled.div`
  margin-top: 26px;
  font-size: 13px;
  font-weight: 500;
  color: ${C.muted};
`;

const Features = styled.ul`
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 11px;
`;

const Feature = styled.li`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.45;
  color: ${C.bodyStrong};

  span {
    width: 18px;
    height: 18px;
    border-radius: 99px;
    flex-shrink: 0;
    margin-top: 1px;
    display: grid;
    place-items: center;
    background: ${({ $featured }) => ($featured ? `color-mix(in srgb, ${C.orange} 16%, ${C.onDark})` : C.orangeWash)};
    color: ${C.orange};
  }
`;

const AddOn = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 44px 48px 40px;
  border-radius: 22px;
  background: ${C.onDark};
  border: 1px solid ${C.line};

  @media (max-width: 780px) {
    padding: 26px 22px 24px;
  }
`;

const AddOnHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 28px;

  @media (max-width: 780px) {
    flex-direction: column;
    gap: 14px;
  }
`;

/* One row per charge, one column per way to pay; the add-on column is the one we want chosen. */
const Matrix = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr 1fr;
  gap: 10px 14px;
  margin-top: 30px;
  align-items: stretch;

  @media (max-width: 760px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;

/* Empty top-left cell; with no row-label column it would push the second header onto its own row */
const Corner = styled.div`
  @media (max-width: 760px) {
    display: none;
  }
`;

const ColHead = styled.div`
  padding: 0 22px 6px;
  font-size: 13.5px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ $hot }) => ($hot ? C.orangeInk : C.bodyStrong)};

  @media (max-width: 560px) {
    padding: 0 14px 4px;
  }
`;

const RowHead = styled.div`
  padding: 20px 0;
  align-self: center;

  strong {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: ${C.deep};
  }
  span {
    display: block;
    margin-top: 4px;
    font-size: 13px;
    line-height: 1.4;
    color: ${C.body};
  }

  @media (max-width: 760px) {
    grid-column: 1 / -1;
    padding: 14px 0 2px;
  }
`;

const Cell = styled.div`
  padding: 20px 22px 18px;
  border-radius: 16px;
  background: ${({ $hot }) => ($hot ? `color-mix(in srgb, ${C.orangeWash} 60%, ${C.onDark})` : C.wash)};
  border: 1px solid ${({ $hot }) => ($hot ? C.orange : C.line)};

  .big {
    font-family: ${SANS};
    font-size: 32px;
    font-weight: 600;
    letter-spacing: -0.03em;
    line-height: 1;
    color: ${C.deep};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .big small {
    font-size: 15px;
    font-weight: 500;
    letter-spacing: 0;
    color: ${C.bodyStrong};
    margin-left: 6px;
  }
  .unit {
    margin-top: 10px;
    font-size: 14px;
    font-weight: 500;
    color: ${C.ink};
  }
  .from {
    margin-top: 3px;
    font-size: 13px;
    color: ${C.body};
  }

  /* Two cells share a phone width; the unbroken "1,000 credits ($10)" line overflowed them */
  @media (max-width: 560px) {
    padding: 16px 14px 14px;

    .big {
      font-size: 26px;
      white-space: normal;
    }
    .big small {
      display: block;
      margin: 6px 0 0;
      font-size: 13.5px;
      line-height: 1.3;
    }
  }
`;

/* Single price tile, used by the top-up card. */
const Tile = styled(Cell)`
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 16px;
    font-size: 13.5px;
    font-weight: 600;
    color: ${C.orangeInk};
  }

  /* Full width on phones, so it keeps the roomy desktop sizing Cell drops there */
  @media (max-width: 560px) {
    padding: 20px 22px 18px;
    .big {
      font-size: 32px;
    }
  }
`;

const TopUp = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 32px;
  align-items: center;

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
    gap: 22px;
  }
`;

const Included = styled.ul`
  list-style: none;
  margin: 30px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 8px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: pp-spin 0.7s linear infinite;
  vertical-align: -2px;

  @keyframes pp-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/* --- page ------------------------------------------------------------------ */

function PlanCard({ plan, yearly, pending, onChoose }) {
  const featured = !!plan.featured;
  const custom = plan.monthly == null;
  const shown = custom ? null : yearly ? Math.round(annualTotal(plan.monthly) / 12) : plan.monthly;

  return (
    <Card $featured={featured}>
      {featured && <Tag>Most popular</Tag>}
      <PlanName>{plan.name}</PlanName>
      <Tagline>{plan.tagline}</Tagline>

      <Price>
        {custom ? (
          <span className="amount">Custom</span>
        ) : plan.trial ? (
          <span className="amount">{usd(0)}</span>
        ) : (
          <>
            <span className="amount">{usd(shown)}</span>
            <span className="per">/seat/month</span>
          </>
        )}
      </Price>
      <BilledNote>
        {custom
          ? "Priced on volume"
          : plan.trial
            ? `For ${TRIAL_DAYS} days`
            : yearly
            ? `Billed ${usd(annualTotal(plan.monthly))}/year`
            : "Billed monthly"}
      </BilledNote>

      <Allowance>
        {custom
          ? "Credits on your terms"
          : `${plan.credits.toLocaleString("en-US")} ${plan.trial ? "credits to start" : "credits/month"}`}
      </Allowance>

      <Cta
        type="button"
        $tone={featured ? "orange" : "outline"}
        disabled={pending}
        onClick={() => onChoose(plan)}
      >
        {pending && <Spinner aria-hidden="true" />}
        {pending ? "One moment" : plan.cta}
      </Cta>

      <Intro aria-hidden={!plan.intro}>{plan.intro || ""}</Intro>
      <Features>
        {plan.features.map((f) => (
          <Feature key={f} $featured={featured}>
            <span>
              <IconCheck size={11} width={2.6} />
            </span>
            {f}
          </Feature>
        ))}
      </Features>
    </Card>
  );
}

export default function PricingPage() {
  const { sessionInvalidated } = useAuth();
  const [yearly, setYearly] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  // This page only shows signed out, so every plan button leads to sign-up.
  const [signupOpen, setSignupOpen] = useState(false);
  const openSignup = () => setSignupOpen(true);
  const [loginOpen, setLoginOpen] = useState(false);
  // Every signed-out visitor gets a 401 on the session check, so the modal must open regardless; the redirect carries reauth.
  const handleLogin = () => setLoginOpen(true);

  const choosePlan = (plan) => {
    if (plan.monthly == null) setDemoOpen(true);
    else openSignup();
  };


  return (
    <MarketingPage>
      <MarketingGlobalStyle />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <SignupModal open={signupOpen} onClose={() => setSignupOpen(false)} />
      <LoginModal
        open={loginOpen}
        reauth={sessionInvalidated}
        onClose={() => setLoginOpen(false)}
        onSignup={() => {
          setLoginOpen(false);
          openSignup();
        }}
      />

      <Nav>
        <NavInner>
          <NavLogo href="/">
            <img src={LOGO} alt="scoutm8" />
          </NavLogo>
          <NavLinks>
            <NavLink href="/#how">How It Works</NavLink>
            <NavLink href="/#features">Product</NavLink>
            <NavLink href="/#copilot">AI Co-pilot</NavLink>
            <NavLink href="/pricing" style={{ color: C.blue }} aria-current="page">
              Pricing
            </NavLink>
          </NavLinks>
          <MarketingNavActions
            links={[
              { href: "/#how", label: "How It Works" },
              { href: "/#features", label: "Product" },
              { href: "/#copilot", label: "AI Co-pilot" },
              { href: "/pricing", label: "Pricing", current: true },
            ]}
            onDemo={() => setDemoOpen(true)}
            onSignup={() => openSignup()}
            onLogin={handleLogin}
          />
        </NavInner>
      </Nav>

      <Hero>
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: -160,
            width: 960,
            height: 520,
            transform: "translateX(-50%)",
            background: `radial-gradient(ellipse at center, color-mix(in srgb, ${C.blue} 12%, transparent), transparent 66%)`,
            pointerEvents: "none",
          }}
        />
        <Container style={{ position: "relative" }}>
          <H1>
            One balance.{" "}
            <Serif style={{ color: C.blue }}>Every tool.</Serif>
          </H1>
          <p
            style={{
              margin: "24px auto 0",
              fontSize: 19,
              lineHeight: 1.55,
              color: C.body,
              maxWidth: 560,
              textWrap: "pretty",
            }}
          >
            Every account starts with a {TRIAL_DAYS}-day free trial and{" "}
            {TRIAL_CREDITS.toLocaleString("en-US")} credits, no card needed. When you're
            ready, pick the monthly allowance that fits your desk.
          </p>
          <Toggle role="group" aria-label="Billing period">
            <ToggleButton type="button" $on={!yearly} aria-pressed={!yearly} onClick={() => setYearly(false)}>
              Monthly
            </ToggleButton>
            <ToggleButton type="button" $on={yearly} aria-pressed={yearly} onClick={() => setYearly(true)}>
              Yearly
              <SavePill>Save {Math.round(ANNUAL_DISCOUNT * 100)}%</SavePill>
            </ToggleButton>
          </Toggle>
        </Container>
      </Hero>

      <section style={{ padding: "8px 0 110px" }}>
        <Container>
          <PlanGrid>
            {PLANS.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                yearly={yearly}
                onChoose={choosePlan}
              />
            ))}
          </PlanGrid>
          <p
            style={{
              margin: "22px auto 0",
              textAlign: "center",
              fontSize: 13.5,
              color: C.muted,
              maxWidth: 620,
            }}
          >
            Prices in US dollars. Change plan or cancel from your account settings at any time.
          </p>
        </Container>
      </section>

      <Section id="add-ons" style={{ background: C.wash }}>
        <Container>
          <Reveal>
            <SectionHead $tight>
              <Eyebrow>Add-ons</Eyebrow>
              <H2>
                Have a target list of firms?{" "}
                <Serif style={{ color: C.orange }}>Track their jobs every day.</Serif>
              </H2>
              <p
                style={{
                  margin: "16px 0 0",
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: C.body,
                  textWrap: "pretty",
                }}
              >
                Hand us the list and every new opening at those firms lands in
                your Jobs tab each morning, for a flat price instead of credits.
                Available on every plan.
              </p>
            </SectionHead>
          </Reveal>
          <Reveal>
            <AddOn>
              <AddOnHead>
                <div style={{ maxWidth: 640 }}>
                  <PlanName>Target list tracking</PlanName>
                  <Tagline style={{ minHeight: 0 }}>
                    Send us your list and every open role at those firms is waiting
                    the moment you register, then watched daily from there.
                  </Tagline>
                </div>
                <PrimaryButton type="button" style={{ flexShrink: 0 }} onClick={() => setDemoOpen(true)}>
                  Book a demo
                </PrimaryButton>
              </AddOnHead>

              <Matrix>
                <Corner />
                <ColHead>Paying with credits</ColHead>
                <ColHead $hot>
                  As an add-on
                  <IconCheck size={15} width={2.4} />
                </ColHead>

                <RowHead>
                  <strong>Setup</strong>
                  <span>We find the firm's careers page and load its open roles.</span>
                </RowHead>
                <Cell>
                  <div className="big">
                    {credits(ADDON_SETUP_CREDITS)}
                    <small>credits ({usd(ADDON_SETUP_CREDITS * CREDIT_USD)})</small>
                  </div>
                  <div className="unit">per company, one-off</div>
                  <div className="from">taken from your credit balance</div>
                </Cell>
                <Cell $hot>
                  <div className="big">{usd(ADDON_SETUP_USD)}</div>
                  <div className="unit">per company, one-off</div>
                  <div className="from">charged once at checkout</div>
                </Cell>

                <RowHead>
                  <strong>Maintenance</strong>
                  <span>Daily checks that keep each firm's roles current.</span>
                </RowHead>
                <Cell>
                  <div className="big">
                    {credits(ADDON_MONTHLY_CREDITS)}
                    <small>credits ({usd(ADDON_MONTHLY_CREDITS * CREDIT_USD)})</small>
                  </div>
                  <div className="unit">/company/month</div>
                  <div className="from">taken from your credit balance</div>
                </Cell>
                <Cell $hot>
                  <div className="big">{usd(ADDON_MONTHLY_USD)}</div>
                  <div className="unit">/company/month</div>
                  <div className="from">added to your subscription bill</div>
                </Cell>
              </Matrix>

              <Included>
                {[
                  "Any firm, from its own careers site",
                  "Checked every day",
                  "Add or drop firms any time",
                  "No credits used",
                ].map((f) => (
                  <Feature key={f}>
                    <span>
                      <IconCheck size={11} width={2.6} />
                    </span>
                    {f}
                  </Feature>
                ))}
              </Included>
            </AddOn>
          </Reveal>
          <Reveal>
            <AddOn style={{ marginTop: 18 }}>
              <TopUp>
                <div>
                  <PlanName>Extra credits</PlanName>
                  <Tagline style={{ minHeight: 0, maxWidth: 560 }}>
                    A busy month running your balance down? Top up in blocks and
                    keep searching. Same credits, spent the same way.
                  </Tagline>
                  <Included style={{ marginTop: 20 }}>
                    {[
                      "Added to your balance straight away",
                      "Buy as many blocks as you need",
                      "Billed with your plan",
                    ].map((f) => (
                      <Feature key={f}>
                        <span>
                          <IconCheck size={11} width={2.6} />
                        </span>
                        {f}
                      </Feature>
                    ))}
                  </Included>
                </div>
                <Tile $hot>
                  <div className="head">
                    Top-up
                    <IconCheck size={15} width={2.4} />
                  </div>
                  <div className="big">{usd(TOPUP_BLOCK_USD)}</div>
                  <div className="unit">per {credits(TOPUP_BLOCK_CREDITS)} credits</div>
                  <div className="from">one-off, buy any time</div>
                  <Cta
                    type="button"
                    $tone="orange"
                    style={{ marginTop: 18, height: 42 }}
                    onClick={openSignup}
                  >
                    Buy credits
                  </Cta>
                </Tile>
              </TopUp>
            </AddOn>
          </Reveal>
        </Container>
      </Section>

      <Section id="faq">
        <Container>
          <Reveal>
            <SectionHead $tight>
              <Eyebrow>FAQ</Eyebrow>
              <H2>Billing, answered.</H2>
            </SectionHead>
          </Reveal>
          <div style={{ maxWidth: 780, margin: "0 auto" }}>
            {FAQS.map((item) => (
              <FaqItem key={item.question} question={item.question} answer={item.answer} />
            ))}
          </div>
        </Container>
      </Section>

      <section>
        <Container>
          <Reveal>
            <CTAPanel>
              <div
                style={{
                  position: "absolute",
                  right: -140,
                  top: -140,
                  width: 460,
                  height: 460,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, color-mix(in srgb, ${C.orange} 42%, transparent), transparent 70%)`,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  left: -160,
                  bottom: -180,
                  width: 460,
                  height: 460,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, color-mix(in srgb, ${C.blue} 55%, transparent), transparent 70%)`,
                }}
              />
              <h2
                style={{
                  position: "relative",
                  fontFamily: SANS,
                  fontWeight: 600,
                  fontSize: "clamp(31px, 5.6vw, 50px)",
                  lineHeight: 1.06,
                  letterSpacing: "-.024em",
                  color: C.onDark,
                  margin: "0 auto",
                  maxWidth: 640,
                  textWrap: "balance",
                }}
              >
                Try it on your own desk,{" "}
                <Serif style={{ color: C.amber }}>free for {TRIAL_DAYS} days.</Serif>
              </h2>
              <p
                style={{
                  position: "relative",
                  margin: "20px auto 0",
                  fontSize: "16.5px",
                  lineHeight: 1.55,
                  color: `color-mix(in srgb, ${C.onDark} 70%, transparent)`,
                  maxWidth: 470,
                }}
              >
                {TRIAL_CREDITS.toLocaleString("en-US")} credits to spend on real searches.
                No card needed.
              </p>
              <CTAButtons>
                <PrimaryButton $lg $onDark type="button" onClick={openSignup}>
                  Start free trial
                </PrimaryButton>
                <OutlineDarkLink as="button" type="button" onClick={() => setDemoOpen(true)}>
                  Book a demo
                </OutlineDarkLink>
              </CTAButtons>
            </CTAPanel>
          </Reveal>
        </Container>
      </section>

      <MarketingFooter onDemo={() => setDemoOpen(true)} />
    </MarketingPage>
  );
}
